/**
 * Per-decoration artwork lifecycle resolver - canonical product editor.
 *
 * Drives the VariantCard `data-state` chip and the ReviewBanner alert
 * state (audit gap #2).
 *
 * Phase F PR-4 — REAL ARTWORK WINS over HASH FALLBACK
 * ====================================================
 *
 * `productLifecycleForVariant` is the new canonical entry point. It
 * accepts (variant, decoration, decorations, artworksStore) and:
 *
 *   1. Asks the artworks store whether a real Artwork record matches
 *      the variant (matched via `artwork_file_id` or `artwork_filename`,
 *      see `stores/artworks.js`).
 *   2. When a match is found AND its `lifecycle_state` is one of the
 *      five canonical Artwork states (draft/in_review/approved/
 *      rejected/archived), THAT wins — bridged into the chip's
 *      "approved"/"in_review" domain via the bridge table below.
 *   3. Otherwise the interim hash rule fires (`productLifecycleFor`,
 *      preserved unchanged). Pre-Phase-F products carry no Artwork
 *      rows and would otherwise have no review surface at all.
 *
 * The bridge: the chip surface today shows only "approved" vs
 * "in_review" (a Day 5 state set). The Artwork state machine has
 * five real states. The bridge is one-way and intentionally narrow:
 *
 *   draft     -> in_review  (work-in-progress IS the review surface for
 *                            the editor — the row needs attention)
 *   in_review -> in_review
 *   approved  -> approved
 *   rejected  -> in_review  (rejected work also needs attention; the
 *                            distinction is surfaced elsewhere)
 *   archived  -> approved   (terminal; treat as "no review needed")
 *
 * When the chip surface expands to render the real five-state alphabet
 * (a follow-up PR), this bridge is the single seam to widen.
 *
 * Backward compatibility — `productLifecycleFor`
 * ===============================================
 *
 * The legacy two-arg entry point (decoration, decorations) is preserved
 * for tests and for callers that don't have an artworks store on hand.
 * It runs the interim hash unconditionally. The new
 * `productLifecycleForVariant` is the surface DecorationInspector +
 * VariantCard reach for in production.
 *
 * interim hash (legacy `productLifecycleFor`)
 * =========================================
 *
 * Day 7 — de-hardcode: exactly ONE decoration per product is "in_review";
 * everything else is "approved". The chosen decoration is the one with
 * the smallest hash of its stable id (`id` or `_tempId`). This is
 * deterministic per-product (same id -> same result across reloads) and
 * varies across products (different id sets -> different chosen
 * decoration). Every product with at least one decoration surfaces the
 * review banner; zero-decoration products hide it.
 *
 * Inputs are intentionally generous:
 *   - `decorations` is the productEditor store's array.
 *   - `decoration` is the row to classify; identification is by `id` or
 *     `_tempId` (mirrors DecorationInspector's `decoId()` helper).
 *
 * Output is the canonical lifecycle string ("approved" or "in_review"),
 * matching the VariantCard chip's `data-state` values.
 */

const APPROVED = "approved";
const IN_REVIEW = "in_review";

// Bridge from the 5-state Artwork machine into the chip's narrow
// 2-state alphabet. Documented in the module header; this table is the
// single seam to widen when the chip surface expands.
const ARTWORK_STATE_TO_CHIP = Object.freeze({
    draft: IN_REVIEW,
    in_review: IN_REVIEW,
    // Issue #2179: an artwork that has been sent for client approval is
    // in-flight and needs attention -> surfaces as "in review" on the
    // chip. Without this key the state fell through to undefined and the
    // placement chip (and the Send-reminder button that keys off it)
    // silently reverted to "approved" after a send.
    awaiting_approval: IN_REVIEW,
    approved: APPROVED,
    rejected: IN_REVIEW,
    archived: APPROVED,
});

function decoId(d) {
    if (!d || typeof d !== "object") return null;
    return d.id ?? d._tempId ?? null;
}

/**
 * Small deterministic non-cryptographic hash of a stable id. Good
 * enough for interim state selection: cheap, stable across reloads,
 * spreads similar ids (e.g. `"42"` vs `"43"`) to different buckets.
 *
 * Folded into a 31-bit space so the result is a safe positive
 * integer suitable for comparison via `<`.
 */
function hashId(id) {
    const s = String(id);
    let h = 0;
    for (let i = 0; i < s.length; i += 1) {
        // Math.imul keeps the multiply within 32 bits.
        h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
    }
    // Day 7 /gate round-1 P1: bare Java String.hashCode is strictly
    // monotonic for same-length strings that differ only in the
    // trailing character (e.g. "1","2","3" or "100","101","102"),
    // which is the realistic shape of decoration ids (sequential DB
    // pkeys, namespaced suffixes). Without an avalanche step, the
    // smallest-hash-wins selector always picked the first id —
    // strictly worse than the pre-Day-7 positional rule (idx === 1).
    // Standard MurmurHash3-style finalizer scatters bits across the
    // whole 32-bit space so adjacent ids land in unrelated buckets.
    h ^= h >>> 16;
    h = Math.imul(h, 0x85ebca6b);
    h ^= h >>> 13;
    h = Math.imul(h, 0xc2b2ae35);
    h ^= h >>> 16;
    // `& 0x7fffffff` strips the sign bit so the result is a
    // non-negative integer suitable for `<` comparison.
    return h & 0x7fffffff;
}

/**
 * Find the id of the single decoration that should be "in_review" for
 * this product, or null when the product has no identifiable
 * decorations. The chosen decoration is the one whose id hashes to the
 * smallest value; ties broken by first-seen order.
 */
function inReviewIdFor(decorations) {
    if (!Array.isArray(decorations) || decorations.length === 0) return null;
    let bestId = null;
    let bestHash = Infinity;
    for (const d of decorations) {
        const id = decoId(d);
        if (id == null) continue;
        const h = hashId(id);
        if (h < bestHash) {
            bestHash = h;
            bestId = id;
        }
    }
    return bestId;
}

/**
 * Return the lifecycle state for a decoration.
 *
 * @param {object} decoration - The decoration row to classify.
 * @param {Array<object>} decorations - All decorations on the product, in
 *   the order DecorationInspector / LeftRail render them.
 * @returns {"approved"|"in_review"} The lifecycle state.
 */
export function productLifecycleFor(decoration, decorations) {
    if (!decoration) return APPROVED;
    const list = Array.isArray(decorations) ? decorations : [];
    const targetId = decoId(decoration);
    if (targetId == null) return APPROVED;
    const flaggedId = inReviewIdFor(list);
    return targetId === flaggedId ? IN_REVIEW : APPROVED;
}

/**
 * Phase F PR-4 canonical entry: real Artwork wins, hash falls back.
 *
 * @param {object|null} variant - The variant row whose state we want.
 * @param {object|null} decoration - The owning decoration row (used by
 *   the hash fallback for per-product flagging).
 * @param {Array<object>} decorations - Sibling decorations (hash input).
 * @param {object|null} artworksStore - The Pinia artworks store, or
 *   anything with a `lifecycleStateForVariant(variant) -> string|null`
 *   method. When omitted/null, behaves exactly like
 *   `productLifecycleFor` (legacy hash-only).
 * @returns {"approved"|"in_review"}
 */
export function productLifecycleForVariant(
    variant,
    decoration,
    decorations,
    artworksStore,
) {
    // Try the real Artwork path first when the caller provides a store.
    // We accept anything that quacks like `lifecycleStateForVariant`
    // (the store's contract) so unit tests can pass a plain stub.
    if (artworksStore && typeof artworksStore.lifecycleStateForVariant === "function") {
        const real = artworksStore.lifecycleStateForVariant(variant);
        if (real && Object.prototype.hasOwnProperty.call(ARTWORK_STATE_TO_CHIP, real)) {
            return ARTWORK_STATE_TO_CHIP[real];
        }
        // `real == null` means "no Artwork match" — fall through to hash.
        // Any unknown state (defensive: bridge table doesn't cover it)
        // ALSO falls through, so the chip never renders garbage.
    }
    return productLifecycleFor(decoration, decorations);
}

const LIFECYCLE_LABELS = Object.freeze({
    approved: "Approved",
    in_review: "In review",
});

/**
 * Human-readable label for a lifecycle state.
 */
export function productLifecycleLabel(state) {
    return LIFECYCLE_LABELS[state] ?? "Approved";
}
